/**
 * TROPAC customisation — see theme/CUSTOMISATIONS.md (tier 4 owned asset).
 *
 * Public MOQ hard floor guard — PDP add-to-cart, cart page, mini-cart and
 * quick-add-bulk quantity inputs (2026-10-19).
 *
 * The Liquid half of the public MOQ (tropac.moq_minimum) already paints the
 * floors: snippets/tropac-product-quantity.liquid sets the PDP input's
 * min/value to max(MOQ, B2B quantity_rule, 1), and
 * snippets/tropac-moq-cart-line-attrs.liquid /
 * snippets/tropac-moq-variant-attrs.liquid set data-min on cart / mini-cart
 * / quick-add-bulk lines. But Athens' product form is `novalidate` and the
 * vendor `quantity-input` element (scripts.js) never enforces `min` on
 * TYPED input, so a buyer could type below the floor, add, and check out.
 * Two such orders (#7125 / #7137) reached NetSuite and were rejected.
 *
 * This file is the JS half. Plain ES2017 IIFE, no dependencies, no build
 * step, idempotent (guards on window.__tropacMoqGuard). Every listener is
 * DELEGATED on document in the CAPTURE phase so it runs before any vendor
 * handler and survives AJAX section re-renders (no re-binding needed):
 *
 *   - submit  (capture): PDP add guard (block a below-floor add, bump the
 *     field to the floor, show the product-form error) + cart / mini-cart
 *     checkout gate (block form submission while a line is below its floor).
 *   - change / focusout (capture): clamp a typed quantity up to the floor
 *     BEFORE the vendor CartItems / mini-cart debounced change handler
 *     reads event.target.value. Floor only — never rounded to multiples.
 *     A PDP clamp dispatches a bubbling 'change' (re-entrancy guarded) so
 *     the vendor sticky-cart copy and the stepper's minus state follow.
 *   - click (capture): keep a quantity-input minus button from stepping
 *     below the floor. On cart / mini-cart lines this only stops the
 *     stepper — removal stays on <cart-remove-button>.
 *   - one MutationObserver on document.body (childList+subtree, attributes
 *     filtered to value/disabled), rAF-throttled, re-runs the checkout gate
 *     after AJAX cart re-renders. Mutations caused by this guard are
 *     ignored so the observer cannot loop.
 *
 * Checkout-gate scoping (2026-10-19 review, B1): the mini-cart drawer
 * renders on EVERY page including /cart, and the vendor only ever
 * re-renders ONE cart container per change — the /cart page's cart-items
 * list after a /cart change, the drawer after a drawer change — so the
 * two containers can hold STALE copies of each other's quantities. The
 * gate therefore never mixes containers: every checkout button is
 * evaluated against its own container's lines only (button inside a
 * <mini-cart> -> that drawer's lines; otherwise <cart-items> lines when
 * the page has a cart list; otherwise every line), and the cart-form
 * submit block uses the same scoping. A stale hidden drawer can never
 * disable the /cart checkout button and vice versa.
 *
 * Floors (effMin): a PDP-family input (name="quantity") carries its floor
 * in the `min` attribute; a cart / mini-cart / quick-add-bulk input carries
 * it in `data-min` and only when one applies — no data-min, no floor. A
 * floor of 1 (no MOQ and no B2B quantity rule) makes every guard here a
 * no-op, so products without an MOQ keep stock Athens behaviour.
 *
 * Non-goals: no checkout Validation Function, no cart-API enforcement
 * (non-theme clients can still bypass), no rounding to multiples, and a
 * value of 0 / blank on a cart line is never touched (Athens
 * remove-line-by-zero behaviour must survive).
 */
(function () {
  'use strict';

  if (window.__tropacMoqGuard) {
    return;
  }
  window.__tropacMoqGuard = { version: 1 };

  var GUARD_MSG_CLASS = 'tropac-moq-guard-msg';
  var CHECKOUT_MSG_CLASS = 'tropac-moq-checkout-msg';
  var DISABLED_ATTR = 'data-tropac-moq-disabled';
  var PREV_ATTR = 'data-tropac-moq-prev';
  var OWN_MSG_MARK = 'data-tropac-moq-own';
  var CHECKOUT_MSG_TEXT =
    'Some items are below their minimum order quantity. ' +
    'Increase them to the minimum (or remove them) to check out.';

  var QTY_SELECTOR =
    'input.quantity-input-field, input[name="quantity"], input[name="updates[]"]';

  // ---------------------------------------------------------------------
  // Floors
  // ---------------------------------------------------------------------

  // PDP-family inputs (the tropac-product-quantity stepper + its sticky-cart
  // clone) are the only name="quantity" inputs on the storefront; the hidden
  // card quick-add quantity input carries `min` only when a MOQ applies
  // (snippets/card-product-add-to-cart.liquid), so it floors to a no-op
  // exactly when there is no MOQ.
  function isPdpQtyInput(input) {
    return input.name === 'quantity';
  }

  function effMin(input) {
    if (isPdpQtyInput(input)) {
      // min is already max(MOQ, variant.quantity_rule.min, 1).
      return parseInt(input.min, 10) || 1;
    }
    if (input.hasAttribute('data-min')) {
      return parseInt(input.getAttribute('data-min'), 10) || 0;
    }
    return 0;
  }

  // ---------------------------------------------------------------------
  // Owned messages
  // ---------------------------------------------------------------------

  function messageAnchor(input) {
    var block = input.closest('.tropac-qty-block');
    if (block) {
      return block;
    }
    var line = input.closest('.cart-item-quantity-wrapper, .mini-cart-item-actions');
    if (line) {
      return line;
    }
    return input.closest('quantity-input') || input;
  }

  function setGuardMsg(input, text) {
    var anchor = messageAnchor(input);
    var parent = anchor.parentNode;
    if (!parent) {
      return;
    }
    var msg = parent.querySelector(':scope > .' + GUARD_MSG_CLASS);
    if (!msg) {
      msg = document.createElement('p');
      msg.className = GUARD_MSG_CLASS;
      msg.setAttribute('role', 'alert');
      parent.insertBefore(msg, anchor.nextSibling);
    }
    msg.textContent = text;
  }

  function clearGuardMsg(input) {
    var anchor = messageAnchor(input);
    var parent = anchor.parentNode;
    if (!parent) {
      return;
    }
    var msg = parent.querySelector(':scope > .' + GUARD_MSG_CLASS);
    if (msg && msg.parentNode) {
      msg.parentNode.removeChild(msg);
    }
  }

  // The PDP prefers the vendor product-form error wrapper (unhidden and
  // marked as ours); the owned <p> fallback covers steppers that live
  // outside a <product-form> (e.g. the sticky-cart clone).
  function showPdpMsg(input, floor, pressAgain) {
    var form = input.form;
    var productForm = form && form.closest ? form.closest('product-form') : null;
    var wrapper = productForm
      ? productForm.querySelector('.product-form-error-message-wrapper')
      : null;
    var text =
      'Minimum order is ' +
      floor +
      '. Quantity set to ' +
      floor +
      (pressAgain ? ' - press Add to cart again.' : '.');
    if (wrapper) {
      var span = wrapper.querySelector('.product-form-error-message');
      if (span) {
        span.textContent = text;
      }
      wrapper.setAttribute(OWN_MSG_MARK, '1');
      wrapper.removeAttribute('hidden');
      return;
    }
    setGuardMsg(input, text);
  }

  // "Clears on next valid change": hides the vendor wrapper only when the
  // message in it is ours — a vendor add-to-cart error is never touched.
  function clearPdpMsg(input) {
    clearGuardMsg(input);
    var form = input.form;
    var productForm = form && form.closest ? form.closest('product-form') : null;
    var wrapper = productForm
      ? productForm.querySelector('.product-form-error-message-wrapper')
      : null;
    if (wrapper && wrapper.getAttribute(OWN_MSG_MARK) === '1') {
      wrapper.setAttribute('hidden', '');
      wrapper.removeAttribute(OWN_MSG_MARK);
    }
  }

  // ---------------------------------------------------------------------
  // Clamp (change + focusout, capture)
  // ---------------------------------------------------------------------

  // Re-entrancy flag for the PDP clamp's own dispatched 'change' (N3): the
  // dispatched event is OURS, not a buyer change, so onQtyChange must not
  // re-clamp / re-message / re-evaluate for it.
  var pdpClampDispatching = false;

  function clampQty(input, viaFocusOut) {
    var floor = effMin(input);
    var value = parseInt(input.value, 10);

    if (isPdpQtyInput(input)) {
      if (floor > 1 && (isNaN(value) || value < floor)) {
        input.value = String(floor);
        // Let the vendor sticky-cart copy of the quantity field and the
        // stepper's minus state see the clamped value (N3) — bubbling so
        // the vendor's own listeners on the input / its ancestors fire.
        if (!pdpClampDispatching) {
          pdpClampDispatching = true;
          try {
            input.dispatchEvent(new Event('change', { bubbles: true }));
          } finally {
            pdpClampDispatching = false;
          }
        }
        showPdpMsg(input, floor, false);
      } else if (!viaFocusOut) {
        clearPdpMsg(input);
      }
      return;
    }

    // Cart / mini-cart / quick-add-bulk: floor only via data-min. A value of
    // 0 or blank is the Athens clear-the-line path — never clamped here.
    if (floor > 1 && value > 0 && value < floor) {
      input.value = String(floor);
      setGuardMsg(
        input,
        'Minimum order is ' + floor + '. Quantity set to ' + floor + '.'
      );
    } else if (!viaFocusOut) {
      clearGuardMsg(input);
    }
  }

  function isQtyInput(target) {
    if (!target || target.tagName !== 'INPUT' || !target.matches) {
      return false;
    }
    return target.matches(QTY_SELECTOR);
  }

  function onQtyChange(event) {
    if (pdpClampDispatching) {
      // Our own dispatched PDP-clamp 'change' — not a buyer change.
      return;
    }
    if (!isQtyInput(event.target)) {
      return;
    }
    clampQty(event.target, false);
    setTimeout(evaluate, 0);
  }

  function onQtyFocusOut(event) {
    if (!isQtyInput(event.target)) {
      return;
    }
    clampQty(event.target, true);
    setTimeout(evaluate, 0);
  }

  // ---------------------------------------------------------------------
  // Minus button (click, capture)
  // ---------------------------------------------------------------------

  function onMinusClick(event) {
    var button = event.target && event.target.closest
      ? event.target.closest('button[name="minus"]')
      : null;
    if (!button) {
      return;
    }
    var stepper = button.closest('quantity-input');
    if (!stepper) {
      return;
    }
    var input = stepper.querySelector('input');
    if (!input) {
      return;
    }
    var floor = effMin(input);
    if (!(floor > 1)) {
      return;
    }
    var value = parseInt(input.value, 10);
    if (isNaN(value) || value <= 0) {
      // 0 / blank is the remove-line path on cart surfaces — never blocked.
      return;
    }
    var step = parseInt(input.step, 10) || 1;
    if (value - step < floor) {
      // Do not go below the floor. On cart / mini-cart lines this only
      // stops the stepper; <cart-remove-button> still removes the line.
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  // ---------------------------------------------------------------------
  // Submit (capture): PDP add guard + cart / mini-cart checkout gate
  // ---------------------------------------------------------------------

  function isCartForm(form) {
    if (form.id === 'cart') {
      return true;
    }
    if (!form.closest) {
      return false;
    }
    return !!(form.closest('cart-items') || form.closest('mini-cart'));
  }

  function isProductAddForm(form) {
    if (form.closest && form.closest('product-form')) {
      return true;
    }
    var action = form.getAttribute('action') || '';
    return action.indexOf('/cart/add') !== -1;
  }

  function onSubmit(event) {
    var form = event.target;
    if (!form || form.tagName !== 'FORM') {
      return;
    }

    if (isCartForm(form)) {
      // Same per-container scoping as evaluate(): a form inside a
      // <mini-cart> drawer is gated by THAT drawer's lines; the /cart form
      // is gated by the <cart-items> lines when the page has a cart list
      // (else every line). A stale sibling container's copy never gates
      // this form (review B1).
      if (findViolations(scopeFor(form)).length > 0) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
      return;
    }

    if (!isProductAddForm(form)) {
      return;
    }

    var input = form.querySelector('input[name="quantity"]');
    if (!input && form.id && window.CSS && window.CSS.escape) {
      input = document.querySelector(
        'input[name="quantity"][form="' + window.CSS.escape(form.id) + '"]'
      );
    }
    if (!input) {
      return;
    }

    var floor = effMin(input);
    if (!(floor > 1)) {
      return;
    }
    var value = parseInt(input.value, 10);
    if (!isNaN(value) && value >= floor) {
      return;
    }

    // Below the floor (or blank): do NOT add the item on this click. Bump
    // the field to the floor, let the vendor stepper update its minus
    // state, and tell the buyer to press Add to cart again.
    event.preventDefault();
    event.stopImmediatePropagation();
    input.value = String(floor);
    input.dispatchEvent(new Event('change', { bubbles: true }));
    showPdpMsg(input, floor, true);
  }

  // ---------------------------------------------------------------------
  // Checkout gate
  // ---------------------------------------------------------------------

  // A line violates when its data-min floor applies and the CURRENT value
  // sits strictly between 0 and the floor. The input's value attribute is
  // the server-rendered quantity, so lines added before this guard shipped
  // (e.g. 5 of a MOQ-10 variant) are detected on page load. 0 / blank is
  // the remove-line intent, never a violation.
  function findViolations(root) {
    var scope = root || document;
    var inputs = scope.querySelectorAll
      ? scope.querySelectorAll('input.quantity-input-field[data-min]')
      : [];
    var violations = [];
    for (var i = 0; i < inputs.length; i++) {
      var input = inputs[i];
      var floor = parseInt(input.getAttribute('data-min'), 10);
      var value = parseInt(input.value, 10);
      if (!isNaN(floor) && value > 0 && value < floor) {
        violations.push(input);
      }
    }
    return violations;
  }

  // Which container's lines gate THIS node (a checkout button or a cart /
  // mini-cart form)? Never a mix of containers — see the header note: the
  // vendor only re-renders ONE cart container per change, so a sibling
  // container can hold a stale copy that must never gate this one.
  //   - inside a <mini-cart> drawer -> that drawer's lines only;
  //   - otherwise, on a page with a <cart-items> list -> those lines only;
  //   - otherwise (no cart list on the page) -> every line.
  function scopeFor(node) {
    var miniCart = node && node.closest ? node.closest('mini-cart') : null;
    if (miniCart) {
      return miniCart;
    }
    if (document.querySelector && document.querySelector('cart-items')) {
      return document.querySelector('cart-items');
    }
    return document;
  }

  function evaluate() {
    var buttons = document.querySelectorAll('button[name="checkout"], #checkout');
    var anyViolations = false;
    for (var i = 0; i < buttons.length; i++) {
      var button = buttons[i];
      // Per-button scoping (review B1): each checkout button is gated by
      // its OWN container's lines, never by a stale sibling's.
      var hasViolations = findViolations(scopeFor(button)).length > 0;
      anyViolations = anyViolations || hasViolations;
      if (hasViolations) {
        if (!button.hasAttribute(DISABLED_ATTR)) {
          // Remember whether the vendor itself wanted this button disabled
          // (cart empty, etc.) so the restore below does not enable a
          // button the vendor disabled independently of the MOQ.
          button.setAttribute(PREV_ATTR, button.disabled ? '1' : '0');
          button.setAttribute(DISABLED_ATTR, '1');
        }
        button.disabled = true;
        if (button.parentNode) {
          var existing = button.parentNode.querySelector(
            ':scope > .' + CHECKOUT_MSG_CLASS
          );
          if (!existing) {
            var msg = document.createElement('p');
            msg.className = CHECKOUT_MSG_CLASS;
            msg.setAttribute('role', 'alert');
            msg.textContent = CHECKOUT_MSG_TEXT;
            button.parentNode.insertBefore(msg, button);
          }
        }
      } else if (button.hasAttribute(DISABLED_ATTR)) {
        button.disabled = button.getAttribute(PREV_ATTR) === '1';
        button.removeAttribute(PREV_ATTR);
        button.removeAttribute(DISABLED_ATTR);
        if (button.parentNode) {
          var stale = button.parentNode.querySelector(
            ':scope > .' + CHECKOUT_MSG_CLASS
          );
          if (stale && stale.parentNode) {
            stale.parentNode.removeChild(stale);
          }
        }
      }
    }
    return anyViolations;
  }

  // ---------------------------------------------------------------------
  // MutationObserver — re-check after AJAX cart re-renders
  // ---------------------------------------------------------------------

  function isOwnMsgNode(node) {
    return (
      node.nodeType === 1 &&
      node.classList &&
      (node.classList.contains(CHECKOUT_MSG_CLASS) ||
        node.classList.contains(GUARD_MSG_CLASS))
    );
  }

  function isOwnMutation(record) {
    if (record.type === 'attributes' && record.attributeName === 'disabled') {
      var target = record.target;
      if (target && target.hasAttribute && target.hasAttribute(DISABLED_ATTR)) {
        return true;
      }
      return false;
    }
    if (record.type === 'childList' && (record.addedNodes.length || record.removedNodes.length)) {
      for (var i = 0; i < record.addedNodes.length; i++) {
        if (!isOwnMsgNode(record.addedNodes[i])) {
          return false;
        }
      }
      for (var j = 0; j < record.removedNodes.length; j++) {
        if (!isOwnMsgNode(record.removedNodes[j])) {
          return false;
        }
      }
      return true;
    }
    return false;
  }

  var rafPending = false;

  function scheduleEvaluate() {
    if (rafPending) {
      return;
    }
    rafPending = true;
    var raf = window.requestAnimationFrame || function (fn) {
      window.setTimeout(fn, 16);
    };
    raf(function () {
      rafPending = false;
      evaluate();
    });
  }

  var bodyObserver = new MutationObserver(function (records) {
    for (var i = 0; i < records.length; i++) {
      if (!isOwnMutation(records[i])) {
        scheduleEvaluate();
        return;
      }
    }
  });

  // ---------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------

  // Dev/test hook ONLY (tests/js/tropac_moq_guard_scope.test.mjs): the
  // flag is never set on the storefront, so this export is inert in
  // production and the behaviour above is byte-identical without it.
  if (window.__TROPAC_MOQ_TEST__) {
    window.__TROPAC_MOQ_TEST__.findViolations = findViolations;
    window.__TROPAC_MOQ_TEST__.scopeFor = scopeFor;
    window.__TROPAC_MOQ_TEST__.evaluate = evaluate;
  }

  function observeAndEvaluate() {
    if (!document.body) {
      return;
    }
    bodyObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['value', 'disabled'],
    });
    evaluate();
  }

  document.addEventListener('submit', onSubmit, true);
  document.addEventListener('change', onQtyChange, true);
  document.addEventListener('focusout', onQtyFocusOut, true);
  document.addEventListener('click', onMinusClick, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeAndEvaluate);
  } else {
    observeAndEvaluate();
  }
})();
